import {
    autoAssign,
    type AutoAssignContext,
    type AutoAssignCriteria,
    dateAdd,
    resetAssignmentState,
} from "@pentacode/core";
import { css, html, LitElement } from "lit";
import { customElement, property, query } from "lit/decorators.js";
import "../../../components/avatar/avatar";
import { Checkbox } from "../../../elements/checkbox";
import "../../../elements/roster-entry";
import "../../../elements/scroller";
import "../../../elements/time-entry-form";
import { app } from "../../../init";
import { debounce } from "../../../lib/util";
import { shared } from "../../../styles";
import type { EmployeeData } from "../types";

@customElement("ptc-auto-assign-menu")
export class AutoAssignMenu extends LitElement {
    private _context: AutoAssignContext;

    @property({ type: Boolean, attribute: "disabled" })
    disabled = false;

    @query("form")
    private _weightsForm: HTMLFormElement;

    private _originalAssignments = new Map<string, number | null>();

    private _originalEmployeeData = new Map<number, EmployeeData>();

    init(context: AutoAssignContext) {
        this._context = context;
        this._originalAssignments.clear();
        this._originalEmployeeData.clear();
        context.entries = context.entries.filter(
            (e) => !e.deleted && e.date >= dateAdd(context.range.from, { days: -1 }) && e.date <= context.range.to
        );
        context.entries.forEach((e) => this._originalAssignments.set(e.id, e.employeeId));
        context.employees.forEach((employeeData) =>
            this._originalEmployeeData.set(employeeData.employee.id, { ...employeeData })
        );

        this.requestUpdate();
        this.updatePreview();
    }

    debouncedUpdatePreview = debounce(() => this.updatePreview(), 200);

    getFormData(fieldName: string) {
        if (!this._weightsForm) {
            return 0;
        }

        const data = new FormData(this._weightsForm);
        return Number(data.get(fieldName) ?? 0);
    }

    updatePreview() {
        const weights: AutoAssignCriteria = {
            matchNominalHours: this.getFormData("matchNominalHours"),
            reduceAccumulatedOvertime: this.getFormData("reduceAccumulatedOvertime"),
            considerAvailabilities: this.getFormData("considerAvailabilities"),
        };

        this._context.entries.forEach((e) => (e.employeeId = this._originalAssignments.get(e.id)!));
        this._context.employees.forEach((e) => {
            const originalData = this._originalEmployeeData.get(e.employee.id);
            if (!originalData) {
                console.log("missing original data for employee", e.employee.id);
                return;
            }

            Object.assign(e, { ...originalData, timeBalance: { ...originalData.timeBalance } });
        });
        this._context.weights = weights;

        const start = performance.now();
        autoAssign(this._context);
        console.log("auto-assign ran in", (performance.now() - start).toFixed(2), "ms");

        this.dispatchEvent(new CustomEvent("updated"));
    }

    async setWeights(weights: Partial<AutoAssignCriteria>) {
        await this.updateComplete;
        const form = this._weightsForm;
        if (!form) {
            return;
        }
        for (const [key, value] of Object.entries(weights)) {
            const input = form.elements.namedItem(key) as HTMLInputElement | null;
            if (input) {
                input.value = String(value);
            }
        }
        this.updatePreview();
    }

    close() {
        this._context.entries.forEach((e) => (e.employeeId = this._originalAssignments.get(e.id)!));
        this._context.employees.forEach((e) => Object.assign(e, this._originalEmployeeData.get(e.employee.id)));
        this.dispatchEvent(new CustomEvent("close"));
    }

    async apply() {
        for (const entry of this._context.entries) {
            if (entry.employeeId !== this._originalAssignments.get(entry.id)) {
                resetAssignmentState(entry);
            }
        }

        await app.createOrUpdateTimeEntries(this._context.entries);
        this.dispatchEvent(new CustomEvent("close"));
    }

    private async _submit(e: Event) {
        e.preventDefault();
        await this.apply();
    }

    static styles = [
        shared,
        Checkbox.styles,
        css`
            :host {
                display: block;
                position: relative;
            }
        `,
    ];

    renderWeightInputs() {
        return html`
            <div class="field">
                <label>Sollstunden erfüllen</label>
                <div class="horizontal layout center-aligning">
                    <input type="range" min="1" max="3" step="1" value="3" name="matchNominalHours" class="stretch" />
                </div>
            </div>
            <div class="field">
                <label>Überstunden abbauen</label>
                <div class="horizontal layout center-aligning">
                    <input
                        type="range"
                        min="1"
                        max="3"
                        step="1"
                        value="2"
                        name="reduceAccumulatedOvertime"
                        class="stretch"
                    />
                </div>
            </div>
            <div class="field">
                <label>Verfügbarkeiten berücksichtigen</label>
                <div class="horizontal layout center-aligning">
                    <input
                        type="range"
                        min="1"
                        max="3"
                        step="1"
                        value="1"
                        name="considerAvailabilities"
                        class="stretch"
                    />
                </div>
            </div>
        `;
    }

    render() {
        return html`
            <div class="fullbleed vertical layout">
                <div class="small vertical layout border-bottom">
                    <button class="slim transparent text-left-aligning" @click=${this.close}>
                        <div class="horizontal layout">
                            <div class="stretch"><ptc-ui-icon name="icon.navigation.back"></ptc-ui-icon> Zurück</div>
                            <div class="key">Esc</div>
                        </div>
                    </button>
                </div>

                <ptc-scroller class="stretch">
                    <form class="padded small" @input=${this.debouncedUpdatePreview} @submit=${this._submit}>
                        <div class="large margined text-centering">
                            <ptc-ui-icon name="icon.action.auto-assign"></ptc-ui-icon> Schichten Automatisch Zuweisen
                        </div>
                        <div class="margined">${this.renderWeightInputs()}</div>
                        <div class="padded vertical layout">
                            <button class="primary" ?disabled=${this.disabled}>Anwenden</button>
                        </div>
                        ${this.disabled
                            ? html`
                                  <ptc-popover trigger="hover" class="tooltip">
                                      <div class="orange colored-text text-left-aligning">
                                          <ptc-ui-icon name="icon.status.warning"></ptc-ui-icon> Die automatische
                                          Schichtzuweisung ist nur in der <strong>Wochenansicht</strong> möglich (Montag
                                          bis Sonntag). Wechseln Sie in die Wochenansicht um fortzufahren.
                                      </div>
                                  </ptc-popover>
                              `
                            : ""}
                    </form>
                </ptc-scroller>
            </div>
        `;
    }
}
